import { SplashScreen as CapacitorSplashScreen } from "@capacitor/splash-screen";
import { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router";

import { SplashScreen } from "@/components/SplashScreen";
import { ConfirmDialogProvider } from "@/components/ui/confirm-dialog";
import { NoInternetConnection } from "@/components/ui/errors";
import { PageLoader } from "@/components/ui/page-loader";
import { Toaster } from "@/components/ui/sonner";
import { readAcquisitionSource } from "@/lib/acquisition";
import mixpanel from "@/lib/mixpanel";
import {
  consumeRedirectAfterAuth,
  rememberRedirectAfterAuth,
} from "@/lib/redirectAfterAuth";
import { getCurrentSession } from "@/lib/sessionAnalytics";
import { LoginHomeMobile } from "@/pages/auth/login/LoginHomeMobile";

import { useSupabaseClient } from "./api/SupabaseProvider";
import { GuestOrProtectedRoute } from "./components/auth/GuestOrProtectedRoute";
import { InviteLinkPrompt } from "./components/auth/InviteLinkPrompt";
// Layout
import { BottomNav } from "./components/layout/BottomNav";
import { DesktopSidebar } from "./components/layout/DesktopSidebar";
import { SlideOverPage } from "./components/ui/slide-over-page";
import { EXPLORE_ENABLED } from "./config/featureFlags";
import { useAppUpdates } from "./hooks/useAppUpdates";
import { useDeepLinks } from "./hooks/useDeepLinks";
import { useIsMobile } from "./hooks/useMediaQuery";
import {
  useNativePushHandler,
  useNativePushAutoPrompt,
  usePushPendingNav,
} from "./hooks/usePushNotifications";
import {
  isResetPasswordAppShell,
  shouldRedirectToPasswordReset,
} from "./lib/authRecovery";
import { hasSeenIntro } from "./lib/firstRunFlags";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import LoginDesktop from "./pages/auth/login/LoginDesktop";
import { RouteByDevice } from "./pages/utils/RouteByDevice";
import { useAuthStore } from "./store/authStore";
import { useBackendStatusStore } from "./store/backendStatusStore";

// Lazy-loaded pages (route-level code splitting)
// Admin routes live in @mash/admin-dashboard; not bundled here.
// These auth sub-routes pull in react-hook-form + zod (~90 kB combined). The
// primary login entry (LoginHomeMobile / LoginDesktop) stays eager so it paints
// instantly; the form-heavy email/signup/forgot flows load on demand.
const EmailLoginMobile = lazyWithRetry(
  () => import("./pages/auth/login/EmailLoginMobile"),
);
const ForgotPasswordRoute = lazyWithRetry(() =>
  import("./pages/auth/ForgotPasswordRoute").then(m => ({
    default: m.ForgotPasswordRoute,
  })),
);
const SignupPage = lazyWithRetry(() =>
  import("./pages/auth/signup/SignupPage").then(m => ({
    default: m.SignupPage,
  })),
);
const Create = lazyWithRetry(() => import("./pages/Create"));
const DataTest = lazyWithRetry(() => import("./pages/DataTest"));
const DesignAppRuntimePage = lazyWithRetry(
  () => import("./pages/DesignAppRuntime"),
);
const Explore = lazyWithRetry(() => import("./pages/Explore"));
const Friends = lazyWithRetry(() => import("./pages/Friends"));
const MobileProfile = lazyWithRetry(() => import("./pages/MobileProfile"));
const DesktopProfile = lazyWithRetry(() => import("./pages/DesktopProfile"));
const JoinPage = lazyWithRetry(() => import("./pages/JoinPage"));
const OnboardingFlow = lazyWithRetry(() => import("./pages/OnboardingFlow"));
const WelcomeIntro = lazyWithRetry(() => import("./pages/WelcomeIntro"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const SettingsProfileSection = lazyWithRetry(
  () => import("./pages/settings/SettingsProfileSection"),
);
const NotificationSettings = lazyWithRetry(
  () => import("./pages/settings/NotificationSettings"),
);
const SettingsAccountSection = lazyWithRetry(
  () => import("./pages/settings/SettingsAccountSection"),
);
const SettingsTermsSection = lazyWithRetry(
  () => import("./pages/settings/SettingsTermsSection"),
);
const SettingsPrivacySection = lazyWithRetry(
  () => import("./pages/settings/SettingsPrivacySection"),
);
const MyShelf = lazyWithRetry(() => import("./pages/MyShelf"));
const TemplatePreview = lazyWithRetry(() => import("./pages/TemplatePreview"));

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  const location = useLocation();

  console.log(
    "[ProtectedRoute] loading:",
    loading,
    "user:",
    user?.email || "null",
  );

  if (loading) {
    return <SplashScreen />;
  }

  if (!user) {
    rememberRedirectAfterAuth(location.pathname + location.search);
    return <Navigate to="/" replace />;
  }

  // Block users who haven't completed the invite flow
  if (!user.inviteAccepted) {
    return <InviteLinkPrompt />;
  }

  // Route first-run users through the onboarding flow before any protected page
  if (!user.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

// Home or Landing based on auth state
// On app domain: logged in → /explore, not logged in → /login
// On landing domain: always Landing (marketing page)
function HomeOrLanding() {
  const { user, loading } = useAuthStore();
  const isMobile = useIsMobile();
  // Latches once the very first auth check resolves, so later sign-in
  // attempts (which also flip `loading`) don't re-trigger the splash. Updated
  // by adjusting state during render rather than in an effect — React's
  // sanctioned pattern, and compatible with the React Compiler.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  if (!loading && !initialLoadDone) {
    setInitialLoadDone(true);
  }

  // Show loading only during initial auth check, not during sign-in attempts
  if (loading && !initialLoadDone) {
    return <SplashScreen />;
  }

  if (user) {
    // Block users who haven't completed the invite flow
    if (!user.inviteAccepted) {
      return <InviteLinkPrompt />;
    }
    // First-run users go through onboarding before landing anywhere
    if (!user.onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }
    const redirect = consumeRedirectAfterAuth();
    const home = "/my-shelf";
    return <Navigate to={redirect ?? home} replace />;
  }

  // First-time mobile visitors see the onboarding intro before the login home.
  if (isMobile && !hasSeenIntro()) {
    return <Navigate to="/welcome" replace />;
  }
  return <Navigate to="/login" replace />;
}

// Wrapper that adds sidebar offset — must be inside BrowserRouter for useLocation
function SidebarOffset({ children }: { children: React.ReactNode }) {
  const { user, recoverySessionActive } = useAuthStore();
  const location = useLocation();
  const hidePaths = [
    "/create",
    "/app/",
    "/join/",
    "/onboarding",
    "/welcome",
    "/t/",
  ];
  const showSidebar =
    (!!user ||
      isResetPasswordAppShell(location.pathname, recoverySessionActive)) &&
    !hidePaths.some(p => location.pathname.startsWith(p));
  return <div className={showSidebar ? "lg:pl-20" : ""}>{children}</div>;
}

// Redirect old invite URLs to /join/:code
function InviteRedirect() {
  const { code } = useParams();
  return <Navigate to={`/join/${code}`} replace />;
}

function useMixpanelIdentify() {
  const { user } = useAuthStore();
  useEffect(() => {
    if (!user) return;
    mixpanel.identify(user.id);

    const acquisitionSource = readAcquisitionSource();
    const session = getCurrentSession();

    // `set_once` — immutable facts about the account. `signupPlatform` is the
    // source of the *first* session we ever identify on, which is the closest
    // proxy we have; nothing records the platform at account-creation time.
    mixpanel.people.set_once({
      email: user.email,
      name: user.displayName,
      ...(user.createdAt && { accountCreatedDate: user.createdAt }),
      ...(acquisitionSource && { acquisitionSource }),
      ...(session && { signupPlatform: session.sessionSource }),
    });

    // `set` — refreshed every session, so dormancy queries
    // (`daysSinceLastSession > 14`) see current values.
    if (session) {
      mixpanel.people.set({
        sessionSource: session.sessionSource,
        ...(session.daysSinceLastSession !== null && {
          daysSinceLastSession: session.daysSinceLastSession,
        }),
      });
    }
  }, [user]);
}

function App() {
  const location = useLocation();
  const { initialize, loading, user, recoverySessionActive } = useAuthStore();
  const supabase = useSupabaseClient();
  const [initError, setInitError] = useState<string | null>(null);
  const backendUnreachable = useBackendStatusStore(s => s.unreachable);
  const markBackendReachable = useBackendStatusStore(s => s.markReachable);

  useMixpanelIdentify();

  // Check for app updates on resume (native only)
  useAppUpdates();

  // Route Universal / App Links into the router instead of the browser (native only)
  useDeepLinks();

  // Handle native push notification taps (must be early, before auth)
  useNativePushHandler();

  // Auto-prompt for native push notifications after login
  useNativePushAutoPrompt(!!user);

  // Handle pending push notification navigation (cold start)
  usePushPendingNav(!!user);

  console.log(
    "[App] Render - loading:",
    loading,
    "user:",
    user?.email || "null",
  );

  useEffect(() => {
    console.log("[App] useEffect - calling initialize...");

    // Safety timeout: if initialize() hangs (e.g., native bridge stall,
    // network timeout), force loading to false so the app doesn't stay
    // stuck on a spinner forever.
    const timeout = setTimeout(() => {
      const { loading: stillLoading } = useAuthStore.getState();
      if (stillLoading) {
        console.warn(
          "[App] Initialize timed out after 10s, forcing loading=false",
        );
        useAuthStore.setState({ loading: false });
        CapacitorSplashScreen.hide().catch(() => {});
      }
    }, 10000);

    initialize(supabase)
      .then(() => {
        clearTimeout(timeout);
        console.log("[App] Initialize completed successfully");
        // Hide splash screen after app is loaded
        CapacitorSplashScreen.hide().catch(() => {
          // Ignore errors (e.g., on web where splash screen isn't available)
        });
      })
      .catch(err => {
        clearTimeout(timeout);
        console.error("[App] Initialize error:", err);
        setInitError(err.message || "Unknown error");
        // Still hide splash screen on error
        CapacitorSplashScreen.hide().catch(() => {});
      });
  }, [initialize, supabase]);

  if (initError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-red-50">
        <div className="text-center">
          <h1 className="text-xl font-bold text-red-600">
            Initialization Error
          </h1>
          <p className="text-red-500">{initError}</p>
        </div>
      </div>
    );
  }

  if (shouldRedirectToPasswordReset(location.pathname, recoverySessionActive)) {
    return <Navigate to="/reset-password" replace />;
  }

  // An API call failed because the backend was unreachable (see `api.ts`). Take
  // over the whole route tree rather than letting each screen invent its own
  // error — the app can't do anything useful until a request gets through.
  //
  // Retry clears the flag rather than reloading: that remounts the routes,
  // which refires their queries, and `api.ts` sets the flag again if the
  // backend is still down. A full reload would also work but restarts auth
  // bootstrap, which is slow on native. Nothing clears this on its own —
  // replacing the route tree unmounts every query, so there is no background
  // refetch left to recover.
  if (backendUnreachable) {
    return <NoInternetConnection fullPage onRetry={markBackendReachable} />;
  }

  return (
    <ConfirmDialogProvider>
      <DesktopSidebar />
      <SidebarOffset>
        {/* Route code-splitting is a mid-session wait, not a cold boot, so it
        gets the neutral "Loading..." screen. `SplashScreen`'s Mash wordmark is
        reserved for the initial auth check above, where the app genuinely is
        starting up. */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/join/:code" element={<JoinPage />} />
            {/* Public, unauthenticated template share preview — must render
            unconditionally for anonymous visitors, no route guard. */}
            <Route path="/t/:templateId" element={<TemplatePreview />} />

            {/* Pre-login onboarding intro (mobile, once per device) */}
            <Route path="/welcome" element={<WelcomeIntro />} />
            {/* First-run flow — self-guarded (not ProtectedRoute, which would
                  redirect back here in a loop) */}
            <Route path="/onboarding" element={<OnboardingFlow />} />
            {/* Backward compat redirects for old invite URLs */}
            <Route path="/invite" element={<Navigate to="/" replace />} />
            <Route path="/invite/:code" element={<InviteRedirect />} />
            <Route path="/app-invite/:code" element={<InviteRedirect />} />
            <Route
              path="/explore"
              element={
                EXPLORE_ENABLED ? (
                  <ProtectedRoute>
                    <Explore />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/my-shelf" replace />
                )
              }
            />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/login"
              element={
                <RouteByDevice
                  mobile={<LoginHomeMobile />}
                  desktop={<LoginDesktop />}
                />
              }
            />
            <Route
              path="/login/email"
              element={
                <RouteByDevice
                  mobile={<EmailLoginMobile />}
                  desktop={<LoginDesktop />}
                />
              }
            />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
            {/* Home — redirects to /login or /explore based on auth state */}
            <Route path="/" element={<HomeOrLanding />} />
            {/* Primary create page (design pipeline) */}
            <Route
              path="/create"
              element={
                <ProtectedRoute>
                  <Create />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create/:sessionId/*"
              element={
                <ProtectedRoute>
                  <Create />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/:appId"
              element={
                <GuestOrProtectedRoute>
                  <SlideOverPage backPath="/">
                    <DesignAppRuntimePage />
                  </SlideOverPage>
                </GuestOrProtectedRoute>
              }
            />
            <Route
              path="/test/data"
              element={
                <ProtectedRoute>
                  <DataTest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/friends"
              element={
                <ProtectedRoute>
                  <Friends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shared"
              element={<Navigate to="/my-shelf/shared" replace />}
            />
            <Route
              path="/templates"
              element={<Navigate to="/my-shelf" replace />}
            />
            <Route path="/my-apps" element={<Navigate to="/" replace />} />
            <Route
              path="/my-shelf/shared"
              element={
                <ProtectedRoute>
                  <MyShelf />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-shelf"
              element={
                <ProtectedRoute>
                  <MyShelf />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <RouteByDevice
                    mobile={<MobileProfile />}
                    desktop={<DesktopProfile />}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <RouteByDevice
                    mobile={null}
                    desktop={<Navigate to="profile" replace />}
                  />
                }
              />
              <Route path="profile" element={<SettingsProfileSection />} />
              <Route path="notifications" element={<NotificationSettings />} />
              <Route path="account" element={<SettingsAccountSection />} />
              <Route path="terms" element={<SettingsTermsSection />} />
              <Route path="privacy" element={<SettingsPrivacySection />} />
            </Route>

            {/* Admin routes moved to @mash/admin-dashboard. */}
          </Routes>
        </Suspense>
        <BottomNav />
      </SidebarOffset>
      <Toaster />
    </ConfirmDialogProvider>
  );
}

export default App;
